import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { meetingAPI, bodyAPI, agendaAPI, attendanceAPI, userAPI, electionAPI, proxyAPI, shareholderAPI } from '../api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { format, parseISO } from 'date-fns';
import {
  ArrowLeft, Users, FileText, Vote, Award, CheckSquare, Clock,
  Play, Square, UserCheck, UserX, Video,
  Plus, Save, Loader2, AlertCircle, CheckCircle, ChevronRight,
  MessageSquare, Gavel, Trash2, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import {
  ConsoleAttendancePanel, ConsoleVotingPanel,
  ConsolePreliminariesTab, ConsoleItemDetailsTab,
  ConsoleElectionTab, ConsoleAdjournmentTab,
} from '../components/console';

const LiveMeetingConsole = () => {
  const { id: meetingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [meeting, setMeeting] = useState(null);
  const [body, setBody] = useState(null);
  const [agendaItems, setAgendaItems] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [rightTab, setRightTab] = useState('preliminaries');
  const [selectedItem, setSelectedItem] = useState(null);
  const [quorumStatus, setQuorumStatus] = useState({ achieved: false, present: 0, required: 0, total: 0 });

  const [preliminaries, setPreliminaries] = useState({});
  const [adjournment, setAdjournment] = useState({});
  const [additionalAgendaInput, setAdditionalAgendaInput] = useState('');

  const [lateArrivals, setLateArrivals] = useState([]);
  const [leftEarly, setLeftEarly] = useState([]);
  const [lateSelectUser, setLateSelectUser] = useState('');
  const [leftEarlySelectUser, setLeftEarlySelectUser] = useState('');

  const [addProxyDialog, setAddProxyDialog] = useState(false);
  const [addElectionDialog, setAddElectionDialog] = useState(false);
  const [proxies, setProxies] = useState([]);
  const [elections, setElections] = useState([]);
  const [shareholders, setShareholders] = useState([]);
  const [newProxy, setNewProxy] = useState({ principal_id: '', proxy_holder_id: '', shares_covered: '', authority_doc: '' });
  const [newElection, setNewElection] = useState({ candidates: '', voting_method: 'straight' });
  const [newAI, setNewAI] = useState({ description: '', responsible: '', group: 'secretariat' });

  const isStockholderMeeting = meeting?.meeting_type === 'asm' || meeting?.meeting_type === 'ssm';

  useEffect(() => {
    if (!selectedItem?.voter_records?.length) return;
    const records = selectedItem.voter_records;
    const forCount = records.filter(r => r.vote === 'for').length;
    const againstCount = records.filter(r => r.vote === 'against').length;
    const abstainCount = records.filter(r => r.vote === 'abstain').length;
    const countField = isStockholderMeeting ? 'shares' : 'votes';
    const needsSync = (selectedItem[`${countField}_for`] || 0) !== forCount ||
                      (selectedItem[`${countField}_against`] || 0) !== againstCount ||
                      (selectedItem[`${countField}_abstain`] || 0) !== abstainCount;
    if (needsSync) {
      const updates = isStockholderMeeting
        ? { shares_for: forCount, shares_against: againstCount, shares_abstain: abstainCount }
        : { votes_for: forCount, votes_against: againstCount, votes_abstain: abstainCount };
      setSelectedItem(prev => ({ ...prev, ...updates }));
      updateAgendaItem(selectedItem.id, updates);
    }
  }, [selectedItem?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [meetingId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (body && attendance.length >= 0) calculateQuorum(); }, [body, attendance]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [meetingRes, agendaRes, attendanceRes, usersRes] = await Promise.all([
        meetingAPI.get(meetingId), agendaAPI.getByMeeting(meetingId),
        attendanceAPI.getByMeeting(meetingId), userAPI.getAll(),
      ]);
      setMeeting(meetingRes.data); setAgendaItems(agendaRes.data);
      setAttendance(attendanceRes.data); setUsers(usersRes.data);
      if (agendaRes.data.length > 0) setSelectedItem(agendaRes.data[0]);
      setPreliminaries(meetingRes.data.preliminaries || {});
      setAdjournment(meetingRes.data.adjournment || {});
      setLateArrivals(meetingRes.data.late_arrivals || []);
      setLeftEarly(meetingRes.data.left_early || []);
      if (meetingRes.data.body_id) {
        const bodyRes = await bodyAPI.get(meetingRes.data.body_id);
        setBody(bodyRes.data);
      }
      const mt = meetingRes.data.meeting_type;
      if (mt === 'asm' || mt === 'ssm') {
        const [proxyRes, electionRes, shRes] = await Promise.all([
          proxyAPI.getByMeeting(meetingId).catch(() => ({ data: [] })),
          electionAPI.getByMeeting(meetingId).catch(() => ({ data: [] })),
          shareholderAPI.getAll().catch(() => ({ data: [] })),
        ]);
        setProxies(proxyRes.data); setElections(electionRes.data); setShareholders(shRes.data);
      } else {
        const electionRes = await electionAPI.getByMeeting(meetingId).catch(() => ({ data: [] }));
        setElections(electionRes.data);
      }
    } catch (err) { console.error('Failed to fetch:', err); toast.error('Failed to load meeting data'); }
    finally { setLoading(false); }
  };

  const calculateQuorum = () => {
    const presentCount = attendance.filter(a => a.status === 'present_onsite' || a.status === 'present_virtual').length;
    const config = body?.quorum_config || { type: 'percentage', value: 50 };
    const total = body?.members?.length || users.length;
    const required = config.type === 'percentage' ? Math.ceil(total * (config.value / 100)) : config.value;
    setQuorumStatus({ achieved: presentCount >= required, present: presentCount, required, total });
  };

  const getUserName = (userId) => users.find(u => u.id === userId)?.full_name || 'Unknown';

  const startMeeting = async () => {
    try {
      const callToOrderTime = new Date().toISOString();
      const updatedPrelim = { ...preliminaries, call_to_order_time: callToOrderTime };
      await meetingAPI.update(meetingId, { status: 'in_progress', preliminaries: updatedPrelim });
      setMeeting({ ...meeting, status: 'in_progress' }); setPreliminaries(updatedPrelim);
      toast.success('Meeting started — Call to Order time recorded');
    } catch { toast.error('Failed to start meeting'); }
  };

  const endMeeting = async () => {
    if (!window.confirm('End this meeting?')) return;
    try {
      const adjTime = new Date().toISOString();
      const updatedAdj = { ...adjournment, adjournment_time: adjTime };
      await meetingAPI.update(meetingId, { status: 'completed', quorum_achieved: quorumStatus.achieved, quorum_value: quorumStatus.present, adjournment: updatedAdj });
      setMeeting({ ...meeting, status: 'completed' }); setAdjournment(updatedAdj);
      toast.success('Meeting ended');
    } catch { toast.error('Failed to end meeting'); }
  };

  const savePreliminaries = async (updates) => {
    const updated = { ...preliminaries, ...updates }; setPreliminaries(updated);
    try { await meetingAPI.update(meetingId, { preliminaries: updated }); toast.success('Saved'); }
    catch { toast.error('Failed to save'); }
  };

  const saveAdjournment = async (updates) => {
    const updated = { ...adjournment, ...updates }; setAdjournment(updated);
    try { await meetingAPI.update(meetingId, { adjournment: updated }); toast.success('Saved'); }
    catch { toast.error('Failed to save'); }
  };

  const addLateArrival = async (userId) => {
    if (!userId || lateArrivals.some(l => l.user_id === userId)) return;
    const u = users.find(x => x.id === userId);
    const entry = { user_id: userId, user_name: u?.full_name || 'Unknown', time: new Date().toISOString() };
    const updated = [...lateArrivals, entry]; setLateArrivals(updated); setLateSelectUser('');
    try { await meetingAPI.update(meetingId, { late_arrivals: updated }); toast.success(`${u?.full_name} marked as late arrival`); }
    catch { toast.error('Failed to save'); }
  };

  const removeLateArrival = async (userId) => {
    const updated = lateArrivals.filter(l => l.user_id !== userId); setLateArrivals(updated);
    try { await meetingAPI.update(meetingId, { late_arrivals: updated }); } catch { toast.error('Failed'); }
  };

  const addLeftEarly = async (userId) => {
    if (!userId || leftEarly.some(l => l.user_id === userId)) return;
    const u = users.find(x => x.id === userId);
    const entry = { user_id: userId, user_name: u?.full_name || 'Unknown', time: new Date().toISOString() };
    const updated = [...leftEarly, entry]; setLeftEarly(updated); setLeftEarlySelectUser('');
    try { await meetingAPI.update(meetingId, { left_early: updated }); toast.success(`${u?.full_name} marked as left early`); }
    catch { toast.error('Failed to save'); }
  };

  const removeLeftEarly = async (userId) => {
    const updated = leftEarly.filter(l => l.user_id !== userId); setLeftEarly(updated);
    try { await meetingAPI.update(meetingId, { left_early: updated }); } catch { toast.error('Failed'); }
  };

  const updateAttendance = async (userId, status) => {
    const existing = attendance.find(a => a.user_id === userId);
    try {
      if (existing) {
        await attendanceAPI.update(existing.id, { status, time_joined: status.includes('present') ? new Date().toISOString() : null });
        setAttendance(attendance.map(a => a.id === existing.id ? { ...a, status } : a));
      } else {
        const res = await attendanceAPI.create({ meeting_id: meetingId, user_id: userId, status, time_joined: status.includes('present') ? new Date().toISOString() : null });
        setAttendance([...attendance, res.data]);
      }
    } catch { toast.error('Failed to update attendance'); }
  };

  const updateAgendaItem = async (itemId, updates) => {
    setSaving(true);
    try {
      await agendaAPI.update(itemId, updates);
      setAgendaItems(items => items.map(i => i.id === itemId ? { ...i, ...updates } : i));
      if (selectedItem?.id === itemId) setSelectedItem(prev => ({ ...prev, ...updates }));
      toast.success('Saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const addActionItemToDetail = () => {
    if (!newAI.description) return;
    const updated = [...(selectedItem?.action_items_detail || []), { ...newAI, id: Date.now().toString() }];
    updateAgendaItem(selectedItem.id, { action_items_detail: updated });
    setNewAI({ description: '', responsible: '', group: 'secretariat' });
  };

  const removeActionItemFromDetail = (aiId) => {
    const updated = (selectedItem?.action_items_detail || []).filter(a => a.id !== aiId);
    updateAgendaItem(selectedItem.id, { action_items_detail: updated });
  };

  const getAttendanceStatus = (userId) => attendance.find(a => a.user_id === userId)?.status || 'absent';

  const createProxy = async () => {
    if (!newProxy.principal_id || !newProxy.proxy_holder_id) { toast.error('Fill proxy fields'); return; }
    try {
      const res = await proxyAPI.create({ meeting_id: meetingId, principal_id: newProxy.principal_id, proxy_holder_id: newProxy.proxy_holder_id, shares_covered: parseInt(newProxy.shares_covered) || 0 });
      setProxies([...proxies, { ...res.data, authority_confirmed: false, authority_doc: newProxy.authority_doc }]);
      setAddProxyDialog(false); setNewProxy({ principal_id: '', proxy_holder_id: '', shares_covered: '', authority_doc: '' });
      toast.success('Proxy recorded');
    } catch { toast.error('Failed to create proxy'); }
  };

  const revokeProxy = async (proxyId) => {
    try { await proxyAPI.revoke(proxyId); setProxies(proxies.map(p => p.id === proxyId ? { ...p, is_valid: false } : p)); toast.success('Proxy revoked'); }
    catch { toast.error('Failed to revoke proxy'); }
  };

  const toggleProxyAuthority = (proxyId) => setProxies(proxies.map(p => p.id === proxyId ? { ...p, authority_confirmed: !p.authority_confirmed } : p));

  const createElection = async () => {
    const names = newElection.candidates.split('\n').filter(c => c.trim());
    if (!names.length) { toast.error('Add candidates'); return; }
    try {
      const res = await electionAPI.create({ meeting_id: meetingId, body_id: meeting.body_id, agenda_item_id: selectedItem?.id, candidates: names.map(n => ({ name: n.trim(), votes: 0, shares_voted: 0 })), voting_method: newElection.voting_method });
      setElections([...elections, res.data]); setAddElectionDialog(false); setNewElection({ candidates: '', voting_method: 'straight' });
      toast.success('Election created');
    } catch { toast.error('Failed to create election'); }
  };

  const updateElectionVotes = async (electionId, idx, field, value) => {
    const election = elections.find(e => e.id === electionId);
    if (!election) return;
    const updated = [...election.candidates]; updated[idx] = { ...updated[idx], [field]: parseInt(value) || 0 };
    try { await electionAPI.update(electionId, { candidates: updated }); setElections(elections.map(e => e.id === electionId ? { ...e, candidates: updated } : e)); }
    catch { toast.error('Failed to update'); }
  };

  const declareWinners = async (electionId) => {
    const election = elections.find(e => e.id === electionId);
    if (!election) return;
    const winners = [...election.candidates].sort((a, b) => (b.votes || 0) - (a.votes || 0)).filter(c => c.votes > 0).map(c => c.name);
    try { await electionAPI.update(electionId, { winners }); setElections(elections.map(e => e.id === electionId ? { ...e, winners } : e)); toast.success(`Winners: ${winners.join(', ')}`); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" /></div>;
  if (!meeting) return <div className="text-center py-16"><AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" /><h2 className="text-xl font-semibold mb-2">Meeting not found</h2><Link to="/meetings"><Button variant="outline">Back to Meetings</Button></Link></div>;

  return (
    <div className="space-y-4" data-testid="live-meeting-console">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white border border-[#E2E6EA] rounded-lg p-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/meetings/${meetingId}`)} className="shrink-0" data-testid="back-to-detail-btn"><ArrowLeft className="h-5 w-5" /></Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-bold text-[#1A1D21]">{meeting.title}</h1>
              <Badge className={meeting.status === 'completed' ? 'bg-blue-100 text-blue-800' : meeting.status === 'scheduled' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                {meeting.status === 'in_progress' ? 'In Progress' : meeting.status.replace('_', ' ')}
              </Badge>
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-[#5A6570]">
              <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{format(parseISO(meeting.date_time), 'MMM d, yyyy h:mm a')}</span>
              {body && <span>{body.name}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`px-4 py-2 rounded-lg border ${quorumStatus.achieved ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-center gap-2">
              {quorumStatus.achieved ? <CheckCircle className="h-5 w-5 text-green-600" /> : <AlertCircle className="h-5 w-5 text-amber-600" />}
              <div>
                <p className={`text-sm font-medium ${quorumStatus.achieved ? 'text-green-800' : 'text-amber-800'}`}>{quorumStatus.achieved ? 'Quorum Achieved' : 'No Quorum'}</p>
                <p className="text-xs text-[#5A6570]">{quorumStatus.present} of {quorumStatus.required} required ({quorumStatus.total} total)</p>
              </div>
            </div>
          </div>
          {meeting.status !== 'completed' && (
            meeting.status === 'in_progress'
              ? <Button onClick={endMeeting} className="bg-red-600 hover:bg-red-700" data-testid="end-meeting-btn"><Square className="h-4 w-4 mr-2" />End Meeting</Button>
              : <Button onClick={startMeeting} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="start-meeting-btn"><Play className="h-4 w-4 mr-2" />Start Meeting</Button>
          )}
        </div>
      </div>

      {/* Main: Left (Attendance) | Right (Discussion Points) */}
      <div className="grid lg:grid-cols-3 gap-4">
        <ConsoleAttendancePanel
          users={users} attendance={attendance} getAttendanceStatus={getAttendanceStatus} updateAttendance={updateAttendance}
          lateArrivals={lateArrivals} addLateArrival={addLateArrival} removeLateArrival={removeLateArrival}
          lateSelectUser={lateSelectUser} setLateSelectUser={setLateSelectUser}
          leftEarly={leftEarly} addLeftEarly={addLeftEarly} removeLeftEarly={removeLeftEarly}
          leftEarlySelectUser={leftEarlySelectUser} setLeftEarlySelectUser={setLeftEarlySelectUser}
          proxies={proxies} revokeProxy={revokeProxy} toggleProxyAuthority={toggleProxyAuthority}
          shareholders={shareholders} getUserName={getUserName} onAddProxy={() => setAddProxyDialog(true)}
        />

        <Card className="border-[#E2E6EA] lg:col-span-2">
          <Tabs value={rightTab} onValueChange={setRightTab}>
            <CardHeader className="pb-0">
              <CardTitle className="text-base mb-2">Discussion Points</CardTitle>
              <TabsList className="tabs-grid-6 bg-[#F0F2F4] p-1">
                <TabsTrigger value="preliminaries" className="data-[state=active]:bg-white text-xs" data-testid="tab-preliminaries">Preliminaries</TabsTrigger>
                <TabsTrigger value="agenda" className="data-[state=active]:bg-white text-xs" data-testid="tab-approved-agenda">Approved Agenda</TabsTrigger>
                <TabsTrigger value="item" className="data-[state=active]:bg-white text-xs" data-testid="tab-item-details">Item Details</TabsTrigger>
                <TabsTrigger value="voting" className="data-[state=active]:bg-white text-xs" data-testid="tab-voting">Voting</TabsTrigger>
                <TabsTrigger value="election" className="data-[state=active]:bg-white text-xs" data-testid="tab-election">Election</TabsTrigger>
                <TabsTrigger value="adjournment" className="data-[state=active]:bg-white text-xs" data-testid="tab-adjournment">Adjournment</TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent className="pt-4">
              <TabsContent value="preliminaries" className="mt-0">
                <ConsolePreliminariesTab
                  preliminaries={preliminaries} setPreliminaries={setPreliminaries} savePreliminaries={savePreliminaries}
                  users={users} attendance={attendance} lateArrivals={lateArrivals} leftEarly={leftEarly} quorumStatus={quorumStatus}
                  additionalAgendaInput={additionalAgendaInput} setAdditionalAgendaInput={setAdditionalAgendaInput}
                />
              </TabsContent>

              <TabsContent value="agenda" className="mt-0">
                <div className="space-y-2">
                  {agendaItems.length === 0 ? (
                    <div className="text-center py-8 text-[#5A6570]"><FileText className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" /><p>No agenda items</p></div>
                  ) : agendaItems.map((item, index) => (
                    <button key={item.id} onClick={() => { setSelectedItem(item); setRightTab('item'); }}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${selectedItem?.id === item.id ? 'bg-[#00A3A3]/5 border-[#00A3A3]' : 'border-[#E2E6EA] hover:border-[#00A3A3]/50'}`}
                      data-testid={`console-agenda-${index}`}>
                      <div className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#00A3A3]/10 text-[#00A3A3] flex items-center justify-center text-xs font-semibold">{index + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-[#1A1D21] truncate">{item.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs px-1.5 py-0.5 rounded bg-[#F0F2F4] text-[#5A6570]">{item.item_type.replace('_', ' ')}</span>
                            {item.presenter && <span className="text-xs text-[#5A6570]">{getUserName(item.presenter)}</span>}
                            {item.motion_text && <Gavel className="h-3 w-3 text-[#F58A26]" />}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-[#A8B1B9] shrink-0" />
                      </div>
                    </button>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="item" className="mt-0">
                <ConsoleItemDetailsTab
                  selectedItem={selectedItem} setSelectedItem={setSelectedItem} updateAgendaItem={updateAgendaItem} users={users}
                  newAI={newAI} setNewAI={setNewAI} addActionItemToDetail={addActionItemToDetail} removeActionItemFromDetail={removeActionItemFromDetail}
                />
              </TabsContent>

              <TabsContent value="voting" className="mt-0">
                <ConsoleVotingPanel
                  selectedItem={selectedItem} setSelectedItem={setSelectedItem} updateAgendaItem={updateAgendaItem}
                  isStockholderMeeting={isStockholderMeeting} attendance={attendance} users={users}
                />
              </TabsContent>

              <TabsContent value="election" className="mt-0">
                <ConsoleElectionTab
                  meetingId={meetingId} meeting={meeting} setMeeting={setMeeting} elections={elections} setElections={setElections}
                  selectedItem={selectedItem} isStockholderMeeting={isStockholderMeeting}
                  addElectionDialog={addElectionDialog} setAddElectionDialog={setAddElectionDialog}
                  newElection={newElection} setNewElection={setNewElection} createElection={createElection}
                  updateElectionVotes={updateElectionVotes} declareWinners={declareWinners} users={users}
                />
              </TabsContent>

              <TabsContent value="adjournment" className="mt-0">
                <ConsoleAdjournmentTab adjournment={adjournment} setAdjournment={setAdjournment} saveAdjournment={saveAdjournment} users={users} />
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </div>

      {/* Proxy Dialog */}
      <Dialog open={addProxyDialog} onOpenChange={setAddProxyDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Proxy</DialogTitle><DialogDescription>Register a proxy and confirm the authority document</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Principal (Shareholder)</Label>
              <Select value={newProxy.principal_id} onValueChange={v => setNewProxy({ ...newProxy, principal_id: v })}>
                <SelectTrigger className="border-[#E2E6EA]"><SelectValue placeholder="Select shareholder" /></SelectTrigger>
                <SelectContent>{shareholders.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({(s.shares || 0).toLocaleString()} shares)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Proxy Holder / Representative</Label>
              <Select value={newProxy.proxy_holder_id} onValueChange={v => setNewProxy({ ...newProxy, proxy_holder_id: v })}>
                <SelectTrigger className="border-[#E2E6EA]"><SelectValue placeholder="Select proxy holder" /></SelectTrigger>
                <SelectContent>
                  {shareholders.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Shares Covered</Label><Input type="number" value={newProxy.shares_covered} onChange={e => setNewProxy({ ...newProxy, shares_covered: e.target.value })} className="border-[#E2E6EA]" /></div>
            <div><Label>Authority Document Reference</Label><Input value={newProxy.authority_doc} onChange={e => setNewProxy({ ...newProxy, authority_doc: e.target.value })} placeholder="e.g., SPA dated March 15, 2026" className="border-[#E2E6EA]" data-testid="proxy-authority-doc" /><p className="text-[10px] text-[#A8B1B9] mt-1">Reference the document that authorizes this proxy</p></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddProxyDialog(false)}>Cancel</Button><Button onClick={createProxy} className="bg-[#00A3A3] hover:bg-[#008585]">Record Proxy</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveMeetingConsole;
